// Third-party
import React, { createRef, RefObject } from 'react';
import { connect } from 'react-redux';

// Components
import QuestionnaireSection from './QuestionnaireSection';
import { SelectableButton } from '../buttons';
import { Typography } from '@capintel/component-lib/typography';

// Util
import { ALLOWED_HTML_TAGS } from '../i18n/FormattedMessageV2';
import { FormattedMessage, formatLocalizedString } from '../../../utils/i18n';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import QuestionItem from '@/types/question-item';
import QuestionOptionItem from '@/types/question-option-item';
import Questionnaire from '@/types/questionnaire';
import GenericContact from '@/types/generic-contact';
import SavedSections from '@/types/saved-sections';
import User from '@/types/user';

type StateProps = {
  locale: Locale;
  user?: Nullable<User>;
};

type DispatchProps = {
  sectionIsDisabled: (section: QuestionnaireSectionItem) => boolean;
  setQuestionnaireComplete: () => void;
  getUnansweredQuestions: (section: QuestionnaireSectionItem) => QuestionItem[];
  getSectionSubtotal: (section: QuestionnaireSectionItem) => number;
  goBackQuestionnaire: () => void;
  saveAnswers: (sectionId: string, questionId: string, option: QuestionOptionItem, text?: string) => void;
  clearAnswers?: (sectionId: string, questionId: string) => void;
  saveNotes?: (sectionId: string, questionId: string, notes?: string) => void;
  clearNotes?: (sectionId: string, questionId: string) => void;
};

type ComponentsProps = {
  customHeader?: React.ReactNode;
};

type Props = StateProps &
  DispatchProps &
  ComponentsProps & {
    questionnaire: Questionnaire;
    scrollQuestionnaire: (question?: QuestionItem) => void;
    contact?: GenericContact;
    userPermissionType?: string;
    isCompleted: boolean;
    savedSections: SavedSections;
  };

type State = {
  selectedSection?: QuestionnaireSectionItem;
  showErrors?: boolean;
};

class QuestionnaireView extends React.Component<Props, State> {
  sectionButtonRefs: Array<RefObject<HTMLButtonElement>>;

  constructor(props: Props) {
    super(props);

    this.sectionButtonRefs = [];
    this.sectionButtonRefs = this.getSortedSections().map((el, i) => this.sectionButtonRefs[i] ?? createRef());
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.selectedSection !== this.state.selectedSection) {
      this.props.scrollQuestionnaire();
    }
  }

  getSortedSections = () => {
    const sections = this.props.questionnaire.sections.slice();
    return sections.sort((a, b) => a.order - b.order);
  };

  readonly state: State = {
    selectedSection: this.getSortedSections()[0],
    showErrors: false,
  };

  completeQuestionnaire = () => {
    this.props.setQuestionnaireComplete();
    this.props.scrollQuestionnaire();
  };

  goNextSection = () => {
    const { getUnansweredQuestions, scrollQuestionnaire } = this.props;
    const { selectedSection } = this.state;

    const sortedSections = this.getSortedSections();

    if (!sortedSections || !selectedSection) {
      return;
    }

    if (getUnansweredQuestions(selectedSection).length > 0) {
      this.setState({ showErrors: true });

      const unansweredQuestions = this.props.getUnansweredQuestions(selectedSection).sort((a, b) => a.order - b.order);
      return scrollQuestionnaire(unansweredQuestions[0]);
    }

    if (this.isLastSection()) {
      return this.completeQuestionnaire();
    }

    this.setState({
      showErrors: false,
      selectedSection: sortedSections[sortedSections.map(s => s._id).indexOf(selectedSection._id) + 1],
    });

    this.sectionButtonRefs[sortedSections.map(s => s._id).indexOf(selectedSection._id)].current?.focus();
  };

  selectSection = (section: QuestionnaireSectionItem) => {
    const { selectedSection } = this.state;

    if (!selectedSection || selectedSection._id !== section._id) {
      this.setState({
        selectedSection: section,
        showErrors: false,
      });
    }
  };

  isLastSection = () => {
    const sortedSections = this.getSortedSections();

    return !!(this.state.selectedSection && this.state.selectedSection._id === sortedSections[sortedSections.length - 1]._id);
  };

  sectionIsSelected = (section: QuestionnaireSectionItem) => this.state.selectedSection && this.state.selectedSection._id === section._id;

  renderSectionButtons = () => {
    const { questionnaire } = this.props;

    return (
      questionnaire.sections &&
      questionnaire.sections.map((section, i) => {
        const isSelected = this.sectionIsSelected(section);

        return (
          <SelectableButton
            key={i}
            ref={this.sectionButtonRefs[i]}
            active={isSelected}
            disabled={this.props.sectionIsDisabled(section)}
            className="section-button"
            onClick={() => this.selectSection(section)}
            aria-label={formatLocalizedString(section.title, this.props.locale)}
            aria-controls={`section-panel-${section._id}`}
            aria-selected={isSelected}
            role="tab"
          >
            <span>{i + 1}</span>
          </SelectableButton>
        );
      })
    );
  };

  skipQuestionnaire = () => {
    this.props.setQuestionnaireComplete();
  };

  render() {
    const { questionnaire, locale, savedSections, customHeader } = this.props;

    const { selectedSection, showErrors } = this.state;
    const isLastSection = this.isLastSection();

    return (
      <div className="questionnaire-view">
        {customHeader}
        <div className="questionnaire-header d-flex align-items-center">
          <h1 className="flex-grow-1" data-private data-dd-privacy="mask">
            {formatLocalizedString(questionnaire.title, locale)}
          </h1>
        </div>

        {(questionnaire.sections.length > 1 || questionnaire.sections[0].title) && (
          <div className="section-header">
            {selectedSection?.title && (
              <h1 className="section-title" id={`section-${selectedSection._id}`} data-private data-dd-privacy="mask">
                <FormattedMessage id="Comparison.Questionnaire.Section.Title" />
                {selectedSection && `${selectedSection.order}: ${formatLocalizedString(selectedSection.title, locale)}`}
              </h1>
            )}

            {selectedSection && selectedSection.description != null && (
              <div className="section-description" data-private data-dd-privacy="mask">
                <Typography as={ALLOWED_HTML_TAGS.H2} variant="bodyMdMedium">
                  {formatLocalizedString(selectedSection.description, locale)}
                </Typography>
              </div>
            )}

            <div className="section-tab-buttons">{this.renderSectionButtons()}</div>
          </div>
        )}

        {selectedSection && (
          <QuestionnaireSection
            questionnaireType={questionnaire.type}
            section={selectedSection}
            isLastSection={isLastSection}
            locale={locale}
            showSubtotal={questionnaire.options.showSectionSubtotal}
            getSectionSubtotal={() => this.props.getSectionSubtotal(selectedSection)}
            goNextSection={this.goNextSection}
            saveAnswers={this.props.saveAnswers}
            clearAnswers={this.props.clearAnswers}
            saveNotes={this.props.saveNotes}
            clearNotes={this.props.clearNotes}
            showErrors={showErrors}
            savedSections={savedSections}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  locale: state.intl.locale,
  user: state.auth.user,
});

export default connect(mapStateToProps)(QuestionnaireView);
